import React, { useState, useEffect } from "react";
import { message, Button, DatePicker, Input, Spin, Card, Divider, Tag } from "antd";
import dayjs from "dayjs";
import { postRequest, getRequest } from "../service/index";
import { getEmployeeId } from "../utils/localStorage";
import { Clock, Calculator, TrendingUp } from "lucide-react";

const { TextArea } = Input;

const RequestOverTime = ({ onClose, onSuccess, initialData = {} }) => {
  const [loading, setLoading] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeList, setEmployeeList] = useState(null);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [requiredHours, setRequiredHours] = useState(9); // Default to 9 hours

  // Get employeeId from localStorage
  const employeeId = getEmployeeId();

  // Use a single formData object for all fields, including details
  const [formData, setFormData] = useState({
    date: initialData.date ? dayjs(initialData.date) : null,
    details: initialData.details || "",
    checkIn: "",
    checkOut: "",
    workingHour: "",
    overtime: "",
  });

  // perhour_salary uses admin form hours; overtime uses shift workingHours + perday_salary (same as payslip).
  const getOvertimeHourlyRate = () => {
    const perDay = parseFloat(employeeData?.perday_salary);
    const shiftHours = Number(requiredHours) > 0 ? Number(requiredHours) : 0;
    if (employeeData && shiftHours > 0 && !Number.isNaN(perDay) && perDay > 0) {
      return perDay / shiftHours;
    }
    return parseFloat(employeeData?.perhour_salary) || 0;
  };

  // Calculate overtime amount based on per hour salary
  const calculateOvertimeAmount = (overtimeHours, perHourSalary) => {
    if (!overtimeHours || !perHourSalary) return 0;

    // Parse overtime hours (format: "HH:MM")
    const [hours, minutes] = overtimeHours.split(':').map(Number);
    const totalOvertimeMinutes = (hours * 60) + minutes;

    // Calculate overtime amount (1x rate per minute)
    const overtimeRate = 1.0; // 1x rate
    const perMinuteSalary = perHourSalary / 60; // Convert hourly rate to per minute
    return (totalOvertimeMinutes * perMinuteSalary * overtimeRate).toFixed(2);
  };

  // Parse time string to hours
  const parseTimeToHours = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours + (minutes / 60);
  };

  // Parse time string to minutes
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours * 60) + minutes;
  };

  // Format hours to time string
  const formatHoursToTime = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Format minutes to time string
  const formatMinutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
  };

  // Safely parse time strings like "HH:MM" or "HH:MM:SS" to total minutes
  const parseTimeToTotalMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const parts = timeStr.split(':').map(Number);
    if (parts.some(part => Number.isNaN(part))) return null;
    const [hours = 0, minutes = 0, seconds = 0] = parts;
    const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;
    return Math.round(totalSeconds / 60);
  };

  // Calculate working hours from check-in and check-out times
  const calculateWorkingHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return "00:00";
    const checkInMinutes = parseTimeToTotalMinutes(checkIn);
    let checkOutMinutes = parseTimeToTotalMinutes(checkOut);
    if (checkInMinutes === null || checkOutMinutes === null) return "00:00";

    // Handle overnight shifts (check-out time is less than check-in time)
    if (checkOutMinutes < checkInMinutes) {
      checkOutMinutes += 24 * 60; // Add 24 hours
    }

    // Calculate working hours
    const workingMinutes = checkOutMinutes - checkInMinutes;
    const hours = Math.floor(workingMinutes / 60);
    const minutes = workingMinutes % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Calculate overtime based on working hours and required hours
  const calculateOvertime = (workingHour, reqHour) => {
    if (!workingHour || !reqHour) return "00:00";

    // Parse working hours (format: "HH:MM")
    const [hours, minutes] = workingHour.split(':').map(Number);
    const workingMinutes = (hours * 60) + minutes;

    // Convert required hours to minutes
    const requiredMinutes = reqHour * 60;

    // Calculate overtime (working minutes - required minutes)
    const overtimeMinutes = Math.max(0, workingMinutes - requiredMinutes);

    const overtimeHours = Math.floor(overtimeMinutes / 60);
    const overtimeMins = overtimeMinutes % 60;

    return `${overtimeHours.toString().padStart(2, '0')}:${overtimeMins.toString().padStart(2, '0')}`;
  };

  // Fetch shift data to get required hours
  useEffect(() => {
    const fetchShiftData = async () => {
      if (!employeeId) return;
      try {
        const response = await getRequest(`/api/companies/shifts/byEmployeeId?employeeId=${employeeId}`);
        if (response?.shift) {
          const shift = response.shift;
          // Use workingHours as required hours for overtime calculation (shift duration)
          // This is the actual shift length, so overtime is calculated beyond this
          const reqHours = shift?.workingHours || 9;
          setRequiredHours(reqHours);
        }
      } catch (error) {
        console.error("Error fetching shift data:", error);
        // Keep default 9 hours on error
      }
    };

    if (employeeId) {
      fetchShiftData();
    }
  }, [employeeId]);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!employeeId) return;
      setEmployeeLoading(true);
      try {
        const response = await getRequest(`/api/employees/${employeeId}`);
        if (response?.employee) {
          setEmployeeData(response.employee);
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        setEmployeeLoading(false);
      }
    };
    const fetchEmployeeList = async () => {
      try {
        const response = await getRequest(`/api/companies/teammembers?employeeId=${employeeId}`);
        if (response?.employee) {
          setEmployeeList(response);
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        setEmployeeLoading(false);
      }
    }
    fetchEmployeeData();
    fetchEmployeeList();
  }, [employeeId]);

  // Update formData when initialData changes
  useEffect(() => {
    setFormData({
      date: initialData.date ? dayjs(initialData.date) : null,
      details: initialData.details || "",
      checkIn: "",
      checkOut: "",
      workingHour: "",
      overtime: "",
    });
    setAttendanceData(null);
    // eslint-disable-next-line
  }, [initialData]);

  // Fetch attendance when date changes
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!employeeId || !formData.date) {
        setAttendanceData(null);
        setFormData(prev => ({
          ...prev,
          checkIn: "",
          checkOut: "",
          workingHour: "",
          overtime: "",
        }));
        return;
      }
      setAttendanceLoading(true);
      try {
        const dateStr = formData.date.format("YYYY-MM-DD");
        const url = `/api/myattendance?employeeId=${employeeId}&startDate=${dateStr}&endDate=${dateStr}`;
        const response = await getRequest(url);
        setAttendanceData(response);

        // Extract the first attendance log if available
        const log = response?.attendanceLogs?.[0];
        if (log) {
          // Calculate working hours and overtime correctly using fetched required hours
          const calculatedWorkingHour = calculateWorkingHours(log.checkIn, log.checkOut);
          const calculatedOvertime = calculateOvertime(calculatedWorkingHour, requiredHours);

          setFormData(prev => ({
            ...prev,
            checkIn: log.checkIn || "",
            checkOut: log.checkOut || "",
            workingHour: calculatedWorkingHour,
            overtime: calculatedOvertime,
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            checkIn: "",
            checkOut: "",
            workingHour: "",
            overtime: "",
          }));
        }
      } catch (error) {
        setAttendanceData(null);
        setFormData(prev => ({
          ...prev,
          checkIn: "",
          checkOut: "",
          workingHour: "",
          overtime: "",
        }));
        // Optionally show error
      } finally {
        setAttendanceLoading(false);
      }
    };
    fetchAttendance();
    // eslint-disable-next-line
  }, [formData.date, employeeId, requiredHours]);

  const handleSubmit = async () => {
    if (!formData.date) {
      message.error("Please select a date");
      return;
    }
    if (!formData.details || formData.details.trim() === "") {
      message.error("Please provide details for your overtime request");
      return;
    }

    // Check if we have attendance data with an ID
    const attendanceLog = attendanceData?.attendanceLogs?.[0];
    if (!attendanceLog?._id) {
      message.error("No attendance log found for the selected date. Please ensure you have checked in/out for this date.");
      return;
    }

    // Use calculated overtime for the request
    const overtimeAmount = calculateOvertimeAmount(formData.overtime, getOvertimeHourlyRate());

    // Prepare payload for overtime request with new structure
    const payload = {
      attendancelogid: attendanceLog._id,
      status: "pending",
      totalOverTime: formData.overtime, // Use calculated overtime
      paidOverTime: overtimeAmount, // Add calculated overtime amount
      details: formData.details.trim(),
    };

    try {
      setLoading(true);
      const response = await postRequest("/api/overtimerequest", payload);
      const managementIds = (employeeList?.managementChain || []).map(m => m?._id).filter(Boolean);
      const uniqueReceiverIds = Array.from(new Set([employeeData?.companyId, ...managementIds].filter(Boolean)));
      if (response.success || response.message === "OvertimeRequest created successfully") {
        const res = await postRequest('/api/notifications', {
          "title": `${employeeList.employee.name} Request OverTime`,
          "senderId": employeeId,
          "receiverIds": uniqueReceiverIds,
          "type": "OverTimeRequest",
          "relatedTypeId": response.overtimeRequest._id,
          "relatedDocumentModel": "OverTimeRequest",
          "companyId": employeeData?.companyId,
          "priority": "medium"
        });
        message.success(response.message || "Overtime request created successfully");
        onSuccess && onSuccess();
        onClose();
      } else {
        message.error(response.error || "Failed to request overtime");
      }
    } catch (error) {
      console.error("Error requesting overtime:", error);
      message.error("Failed to request overtime. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Calculate overtime breakdown
  const overtimeBreakdown = () => {
    if (!employeeData || !formData.overtime) return null;

    const perHourSalary = getOvertimeHourlyRate();
    const overtimeMinutes = parseTimeToMinutes(formData.overtime);
    const overtimeAmount = calculateOvertimeAmount(formData.overtime, perHourSalary);
    const perMinuteSalary = perHourSalary / 60;

    return {
      perHourSalary,
      perMinuteSalary,
      overtimeMinutes,
      overtimeAmount: parseFloat(overtimeAmount),
      totalAmount: parseFloat(overtimeAmount) // Only overtime amount, no regular amount
    };
  };

  const breakdown = overtimeBreakdown();

  return (
    <Spin spinning={loading || attendanceLoading || employeeLoading}>
      <div className="flex flex-col gap-4 max-h-[80vh] overflow-y-auto pr-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="date" className="text-md">
            Select Date
          </label>
          <DatePicker
            id="date"
            className="w-full"
            placeholder="Select date"
            value={formData.date}
            onChange={value => setFormData({ ...formData, date: value })}
            allowClear
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            disabled={loading}
          />
        </div>

        {/* Show attendance fields if attendanceData is loaded */}
        <div>
          {!attendanceLoading && attendanceData && Array.isArray(attendanceData.attendanceLogs) && attendanceData.attendanceLogs.length === 0 && (
            <div className="text-gray-500 text-sm">No logs available</div>
          )}
          {!attendanceLoading && attendanceData && attendanceData.attendanceLogs && attendanceData.attendanceLogs.length > 0 && (
            <Card
              title={
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-white" />
                  <span>Attendance Details</span>
                </div>
              }
              className="bg-blue-50 border-blue-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Check In</label>
                  <Input
                    value={formData.checkIn}
                    placeholder="Check In"
                    disabled
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Check Out</label>
                  <Input
                    value={formData.checkOut}
                    placeholder="Check Out"
                    disabled
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Working Hour</label>
                  <Input
                    value={formData.workingHour}
                    placeholder="Working Hour"
                    disabled
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Overtime</label>
                  <Input
                    value={formData.overtime}
                    placeholder="Overtime"
                    disabled
                  />
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Overtime Calculation Breakdown */}
        {breakdown && (
          <Card
            title={
              <div className="flex items-center gap-2">
                <Calculator size={16} className="text-white" />
                <span>Overtime Calculation</span>
              </div>
            }
            className="bg-purple-50 border-purple-200 mb-2"
          >
            <div
              className="flex flex-col md:flex-row gap-4"
              style={{ width: "99%" }}
            >
              <div className="space-y-2 flex-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Per Hour Rate:</span>
                  <span className="font-semibold">RS {breakdown.perHourSalary.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Per Minute Rate:</span>
                  <span className="font-semibold">RS {breakdown.perMinuteSalary.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Overtime Minutes:</span>
                  <span className="font-semibold">{breakdown.overtimeMinutes} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Overtime Rate (1x):</span>
                  <span className="font-semibold">RS {breakdown.perMinuteSalary.toFixed(2)}/min</span>
                </div>
              </div>
              <div className="space-y-2 flex-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Overtime Amount:</span>
                  <span className="font-semibold text-green-600">RS {breakdown.overtimeAmount.toFixed(2)}</span>
                </div>
                <Divider />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Amount:</span>
                  <span className="text-green-600">RS {breakdown.totalAmount.toFixed(2)}</span>
                </div>
                <Tag color="green" className="w-full text-center">
                  <TrendingUp size={14} className="mr-1" />
                  Overtime: {formatMinutesToTime(breakdown.overtimeMinutes)} at 1x rate
                </Tag>
              </div>
            </div>
          </Card>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor="details" className="text-md">
            Details
          </label>
          <TextArea
            id="details"
            placeholder="Enter details for your overtime request"
            value={formData.details}
            onChange={e => setFormData({ ...formData, details: e.target.value })}
            rows={4}
            maxLength={500}
            showCount
            disabled={loading}
          />
        </div>

        <div className="flex items-center justify-end gap-3 my-2">
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            type="primary"
            loading={loading}
            className="bg-primary text-white"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default RequestOverTime;
